import { FunctionComponent, useId } from 'react';
import { TableRow, TableCell } from '../../../tailwindcomponents';
import { ExtraTaskInWorkpackages } from '../../../types/line';
import { redirectToMxi } from '../../../config/mxiProvider';

type WorkPackageTasksPanelProps = {
  tasks: Array<ExtraTaskInWorkpackages>;
  colSpan: number;
};

// The inner table sits inside `.ds-table`, whose `thead th` rule is sticky -
// left alone, these headers would pin to the top of the page scroller.
const thClass = '!static !h-8';

/**
 * The tasks inside one work package, shown as a card in a single full-width row
 * under the package. Its own headers, because these are task fields, not
 * work-package fields.
 */
const WorkPackageTasksPanel: FunctionComponent<WorkPackageTasksPanelProps> = ({ tasks, colSpan }) => {
  // Names the inner table after the visible card title, so screen readers
  // don't announce it as an unnamed table nested in the main one.
  const titleId = useId();

  return (
    <TableRow className="!bg-transparent hover:!bg-transparent">
      <TableCell colSpan={colSpan} className="!h-auto !p-0">
        {/* w-0 + min-w-full: the panel contributes nothing to the cell's
         * min-content width, so the parent table's auto layout never widens a
         * column to fit it - opening a row cannot shift the columns. */}
        <div className="w-0 min-w-full">
          <div className="ds-card my-2 ml-10 mr-2 overflow-hidden">
            <div id={titleId} className="ds-card-header !py-2 text-sm font-semibold">
              Tasks in this package ({tasks.length})
            </div>
            {tasks.length === 0 ? (
              <div className="ds-card-body text-sm text-[color:var(--ds-text-muted)]">No tasks</div>
            ) : (
              <table aria-labelledby={titleId} className="w-full table-fixed">
                <colgroup>
                  <col className="w-[38%]" />
                  <col className="w-[14%]" />
                  <col className="w-[16%]" />
                  <col className="w-[32%]" />
                </colgroup>
                <thead>
                  <tr>
                    <th className={thClass}>Task name</th>
                    <th className={thClass}>Barcode</th>
                    <th className={thClass}>Start date</th>
                    <th className={thClass}>Due date</th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.map((task, idx) => (
                    <tr key={`${task.barcode}-${idx}`}>
                      <td className="truncate" title={task.name}>{task.name}</td>
                      <td className="truncate">
                        <button
                          type="button"
                          className="cursor-pointer border-0 bg-transparent p-0 text-[color:var(--ds-accent)] hover:underline"
                          title="Open task in MXI"
                          onClick={() => redirectToMxi(task.barcode)}
                        >
                          {task.barcode}
                        </button>
                      </td>
                      <td className="truncate">{task.start_date}</td>
                      <td className="truncate">{task.due_date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </TableCell>
    </TableRow>
  );
};

export default WorkPackageTasksPanel;
